import ModalContainer from '@/components/molecules/ModalContainer';
import { Center, HStack, Image, Text, VStack } from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import CustomButton from '@/components/atoms/CustomButton';
import colors from '@/theme/colors';
import useRequestPeerSupportContact from '@/hooks/contact/useRequestPeerSupportContact';
import RadioButtonGroup from '@/components/molecules/RadioButtonGroup';
import { TPeerSupportTeamMemberInfo } from '@/types/PeerSupport.types';
import { text } from '@/theme/colors/palettes';
import DefaultAvatar from '@/components/atoms/DefaultAvatar';

const SELECTED_ITEM_DEFAULT = undefined;

const REQUEST_CONTACT_OPTIONS = ['Phone Call', 'Text Message'] as const;
type TRequestContactOption = (typeof REQUEST_CONTACT_OPTIONS)[number] | undefined;

const CONTENT_WIDTH = '90%';

interface Props {
  selectedPeerSupport: TPeerSupportTeamMemberInfo;
  triggerButtonLabel?: string;
  triggerButtonWidth?: string;
  backgroundColor?: string;
  labelColor?: string;
  hoveredBackgroundColor?: string;
  hoveredLabelColor?: string;
  triggerButtonTransparent?: boolean;
  noShadow?: boolean;
}

const RequestContactModalWithTrigger = ({
  selectedPeerSupport,
  triggerButtonLabel = 'Request Contact',
  triggerButtonWidth = '340px',
  backgroundColor = colors.extra.white,
  labelColor = colors.primary[500],
  hoveredBackgroundColor = colors.primary[500],
  hoveredLabelColor = colors.extra.white,
  triggerButtonTransparent = false,
  noShadow = false,
}: Props) => {
  const [requestContactModalVisible, setRequestContactModalVisible] = useState(false);
  const [selectedItem, setSelectedItem] = useState<TRequestContactOption>();

  useEffect(() => {
    if (!requestContactModalVisible) {
      setIsSuccess(false);
      setSelectedItem(SELECTED_ITEM_DEFAULT);
    }
  }, [requestContactModalVisible]);

  const { mutate, isPending } = useRequestPeerSupportContact({
    onSuccessCallback: () => {
      setIsSuccess(true);

      setSelectedItem(SELECTED_ITEM_DEFAULT);
    },
  });

  const [isSuccess, setIsSuccess] = useState(false);

  const onSubmit = () => {
    if (isSuccess) {
      setRequestContactModalVisible(false);
    } else {
      mutate({
        peerSupportId: selectedPeerSupport?.id ?? '',
        contactMethod: selectedItem === 'Phone Call' ? 'call' : 'text',
      });
    }
  };

  return (
    <>
      <CustomButton
        isTransparent={triggerButtonTransparent}
        label={triggerButtonLabel}
        onClick={() => setRequestContactModalVisible(true)}
        backgroundColor={backgroundColor}
        labelColor={labelColor}
        width={triggerButtonWidth}
        style={{ border: `2px solid ${triggerButtonTransparent ? labelColor : backgroundColor}` }}
        hoverBackgroundColor={hoveredBackgroundColor}
        labelHoverColor={hoveredLabelColor}
        noShadow={noShadow}
      />
      <ModalContainer
        isOpen={requestContactModalVisible}
        onClose={() => setRequestContactModalVisible(false)}
        isBlur={false}
        width={'450px'}
        backgroundColor={'background.lightBlue'}
      >
        <VStack width={'100%'} minHeight={'100%'} gap={'20px'} justify={'space-between'} paddingBottom={'20px'}>
          <Text variant={'loraTitle'} textAlign={'center'} fontSize={'26px'} marginTop={'30px'} paddingX={'40px'}>
            Request Contact
          </Text>
          <VStack width={CONTENT_WIDTH} marginBottom={'6px'}>
            <Center
              width="100%"
              gap={6}
              padding={'16px'}
              rounded={'10px'}
              style={{ boxShadow: '0px 8px 40px 0px #0000001A' }}
              backgroundColor={'white'}
            >
              {selectedPeerSupport?.profileImage ? (
                <Image
                  src={selectedPeerSupport?.profileImage}
                  alt="peer-support-modal-avatar"
                  style={{
                    width: 72,
                    height: 72,
                    margin: '2px',
                    borderRadius: '50%',
                    padding: '2px',
                    backgroundColor: 'white',
                  }}
                  objectFit={'cover'}
                  boxShadow="0px 6px 24px 0px #0E006233"
                />
              ) : (
                <DefaultAvatar containerSize={70} />
              )}
              <VStack alignItems={'start'}>
                <Text variant="loraSmallTitle" color={text.darkBlue}>{`${selectedPeerSupport?.name}`}</Text>
                <VStack alignItems={'center'} textAlign={'start'}>
                  <Text variant="urbanistMedium" color={text.mediumGray}>
                    Peer Support
                  </Text>
                </VStack>
              </VStack>
            </Center>
          </VStack>

          {isSuccess ? (
            <VStack width={CONTENT_WIDTH} gap={4}>
              <Text variant="urbanistMediumLarge" color={text.darkBlue} textAlign={'center'}>
                Notification has been sent, they should be reaching out soon.
              </Text>
            </VStack>
          ) : (
            <VStack width={CONTENT_WIDTH} gap={4}>
              <Text variant="loraExtraSmallTitle">How would you like to be contacted?</Text>
              <HStack width={'100%'}>
                <RadioButtonGroup
                  borderWidth={2}
                  width={'170px'}
                  items={REQUEST_CONTACT_OPTIONS as unknown as string[]}
                  selectedItem={selectedItem ?? ''}
                  onClick={(item: string) => setSelectedItem(item as TRequestContactOption)}
                />
              </HStack>
            </VStack>
          )}

          <HStack width={'100%'} justifyContent={'center'}>
            <CustomButton
              label={isSuccess ? 'Close' : 'Confirm'}
              onClick={() => onSubmit()}
              backgroundColor={'primary.500'}
              disabled={isSuccess ? false : !selectedItem}
              labelColor={'white'}
              width={CONTENT_WIDTH}
              isLoading={isPending}
            />
          </HStack>
        </VStack>
      </ModalContainer>
    </>
  );
};

export default RequestContactModalWithTrigger;
